import { type BoardState } from '@shared/board'
import { type Rect, union } from '@shared/whiteboard/geometry'
import { type MaterialRef, parseMaterialHref } from '@shared/material'

// The pointing language of AI messages (DESIGN.md "指涉"): markdown links with
// item://, edge:// and region:// targets. A region is only a pointer.

export type Mention =
  | { kind: 'item'; id: string; quote?: string }
  | { kind: 'edge'; id: string }
  | { kind: 'items'; ids: string[] }
  | { kind: 'rect'; rect: Rect }

export const MENTION_PROTOCOLS = ['item:', 'edge:', 'region:']

export function parseMention(href: string): Mention | null {
  let url: URL
  try {
    url = new URL(href)
  } catch {
    return null
  }
  // For non-special schemes the "host" holds the first segment: item://ID -> host ID.
  const rest = decodeURIComponent((url.host + url.pathname).replace(/^\/+/, ''))
  if (url.protocol === 'item:') {
    const quote = new URLSearchParams(url.hash.slice(1)).get('q') ?? undefined
    return rest ? { kind: 'item', id: rest, ...(quote && { quote }) } : null
  }
  if (url.protocol === 'edge:') return rest ? { kind: 'edge', id: rest } : null
  if (url.protocol === 'region:') {
    const [mode, spec = ''] = rest.split('/')
    if (mode === 'items') {
      const ids = spec.split(',').filter(Boolean)
      return ids.length ? { kind: 'items', ids } : null
    }
    if (mode === 'rect') {
      const [x, y, w, h] = spec.split(',').map(Number)
      // An area has a place and a size: finite numbers, and some width and height.
      if (
        x !== undefined &&
        y !== undefined &&
        w !== undefined &&
        h !== undefined &&
        [x, y, w, h].every((n) => Number.isFinite(n)) &&
        w > 0 &&
        h > 0
      )
        return { kind: 'rect', rect: { x, y, w, h } }
    }
  }
  return null
}

export type TextPart =
  | { kind: 'text'; text: string }
  /** A board pointer, written as a link or (by mistake, outside the chat) as an embed. */
  | { kind: 'mention'; mention: Mention; label: string }
  /** A place in a material (`mat://…`, MATERIALS.md). */
  | { kind: 'material'; ref: MaterialRef; label: string }

/**
 * Plain text with its board pointers picked out: `[label](item://…)`,
 * `[A → B](edge://…)`, `[here](region://…)`, and embeds `![…](…)`. Anything
 * else, including ordinary web links, stays text. For places that show text
 * as written rather than as markdown (messages on the board).
 */
export function splitMentions(text: string): TextPart[] {
  const parts: TextPart[] = []
  const link = /!?\[([^\]]*)\]\(([^)\s]*)\)/g
  let at = 0
  for (let m = link.exec(text); m; m = link.exec(text)) {
    const mention = parseMention(m[2] ?? '')
    const ref = mention ? null : parseMaterialHref(m[2] ?? '')
    if (!mention && !ref) continue
    if (m.index > at) parts.push({ kind: 'text', text: text.slice(at, m.index) })
    if (mention) parts.push({ kind: 'mention', mention, label: m[1] ?? '' })
    else if (ref) parts.push({ kind: 'material', ref, label: m[1] ?? '' })
    at = m.index + m[0].length
  }
  if (at < text.length) parts.push({ kind: 'text', text: text.slice(at) })
  return parts
}

/** Item ids a mention points at (for highlighting). */
export function mentionIds(m: Mention, s: BoardState): string[] {
  switch (m.kind) {
    case 'item':
      return [m.id]
    case 'edge': {
      const e = s.edges.get(m.id)
      return e ? [e.from, e.to] : []
    }
    case 'items':
      return m.ids
    case 'rect':
      return []
  }
}

/** The board rectangle a mention covers, or null when its target is gone. */
export function mentionRect(m: Mention, s: BoardState): Rect | null {
  if (m.kind === 'rect') return m.rect
  const items = mentionIds(m, s)
    .map((id) => s.items.get(id))
    .filter((i) => !!i)
  return union(items)
}
